import React, { useCallback, useEffect } from "react";
import styles from "./index.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getCoursesByUser,
  getFeaturedCourses,
} from "../../../redux/features/course/courseService";
import SkeletonCourseCard from "../../Skeleton/CourseCardSekeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { selectUserDetails } from "../../../redux/features/auth/authSlice";
import { faClock, faUserGroup } from "@fortawesome/free-solid-svg-icons";

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const FeaturedCourses = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isCoursesLoading, courses } = useSelector(
    (state) => state.course.featuredCourses
  );
  const { roleName } = useSelector(selectUserDetails);
  const { isPurchasedCoursesLoading, purchasedCourses } = useSelector(
    (state) => state.course.purchasedCourses
  );

  const fetchFeaturedCourses = useCallback(
    (filters = {}) => {
      const params = {
        skip: 0,
        limit: 3,
        ...filters,
      };
      dispatch(getFeaturedCourses(params));
    },
    [dispatch]
  );

  useEffect(() => {
    fetchFeaturedCourses();
  }, [fetchFeaturedCourses]);

  useEffect(() => {
    if (roleName === "user") {
      dispatch(getCoursesByUser());
    }
  }, [dispatch, roleName]);

  const openCourse = (slug) => {
    navigate(`/courses/${slug}`);
  };

  const isPurchased = (courseId) => {
    return purchasedCourses?.some((pc) => pc?.course?.id === courseId);
  };

  const openMyCourse = (slug) => {
    navigate(`/my-courses/${slug}`);
  };

  const openMentor = (id) => {
    navigate(`/mentors/${id}`);
  };

  const isSingleCourse = courses?.length === 1;

  return (
    <>
      {isCoursesLoading ? (
        <div className={styles.homeContainer}>
          <div className={`main-container`}>
            <div className={`container-content`}>
              <div className={styles.homeHeader}>
                <h1>Featured Courses</h1>
              </div>
              <div className={styles.courseList}>
                {Array.from({ length: isSingleCourse ? 1 : 1 }).map((_, index) => (
                  <SkeletonCourseCard key={index} isSingle={true} />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        courses?.length > 0 && (
          <div className={styles.homeContainer}>
            <div className={`main-container`}>
              <div className={`container-content`}>
                <div className={styles.homeHeader}>
                  <h1 className={styles.homeTitle}>Featured Courses</h1>
                  <p className={styles.homeSubtitle}>
                    Discover a standout learning experience crafted to inspire action.
                  </p>
                </div>
                <div className={`${styles.courseList} ${isSingleCourse ? styles.singleCourseList : ""}`}>
                  {courses?.map((course) => (
                    <article
                      className={`${styles.courseBoxCard} ${isSingleCourse ? styles.singleCourseBoxCard : ""}`}
                      key={course?.id}
                    >
                      <div className={styles.imageWrapper}
                        onClick={() => { { isPurchased(course?.id) ? openMyCourse(course.slug) : openCourse(course.slug) } }}>
                        <img
                          src={course?.image_url}
                          alt={course?.title}
                          className={styles.courseImg}
                        />
                        <div className={styles.imageOverlay} />
                        <span className={styles.featureBadge}>Featured</span>
                        {isSingleCourse && (
                          <div className={styles.heroTags}>
                            <span>Expert-led</span>
                            <span>Flexible</span>
                          </div>
                        )}
                      </div>
                      <div className={styles.detailscard}>
                        <div className={styles.courseDetails}>
                          <div className={styles.metaRow}>
                            <span className={styles.categoryPill}>Popular pick</span>
                            <span className={styles.ratingPill}>New</span>
                          </div>
                          <p className={styles.coursetitle}>{course?.title}</p>
                          <p className={styles.courseDescription}>
                            {course?.description
                              ?.split(" ")
                              .slice(0, 35)
                              .join(" ") + "..."}
                          </p>
                          <div className={styles.mentorRow}>
                            <span className={styles.mentorLabel}>Mentor</span>
                            <p className={styles.userName} onClick={() => openMentor(course?.user?.id)}>
                              {course?.user?.full_name}
                            </p>
                          </div>
                          <div className={styles.iconRow}>
                            <p className={styles.detailsRow}>
                              <FontAwesomeIcon
                                icon={faUserGroup}
                                className={styles.rowIcon}
                              />
                              {course?.download === 0 ? 10 : course?.download}
                            </p>
                            <p className={styles.detailsRow}>
                              <FontAwesomeIcon
                                icon={faClock}
                                className={styles.rowIcon}
                              />
                              {course?.duration}
                            </p>
                          </div>
                          <div className={styles.bottomRow}>
                            <div className={styles.priceBlock}>
                              <span className={styles.price}>
                                Rs. {course?.discount_price > 0 ? formatNumber(course?.discount_price) : formatNumber(course?.price)}
                              </span>
                              <span className={styles.oldPrice}>
                                {course?.discount_price > 0 ? `Rs. ${course?.price?.toFixed(2)}` : ""}
                              </span>
                            </div>
                            {isPurchased(course?.id) ? (
                              <button
                                type="button"
                                className={styles.buyBtn}
                                onClick={() => openMyCourse(course.slug)}
                              >
                                Resume
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={styles.buyBtn}
                                onClick={() => openCourse(course.slug)}
                              >
                                Enroll now
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default FeaturedCourses;
